import ApiService from "@/services/api.service";

// ACTIONS
export const LIST_API_KEYS = "listApiKeys";
export const LIST_API_SCOPES = "listApiScopes";
export const LIST_API_UTENTI = "listApiUtenti";
export const CREATE_API_KEY = "createApiKey";
export const UPDATE_API_KEY = "updateApiKey";
export const DELETE_API_KEY = "deleteApiKey";

// MUTATIONS
export const SET_LIST_API_KEYS = "setListApiKeys";
export const SET_LIST_API_SCOPES = "setListApiScopes";
export const SET_LOADING = "setLoading";
export const SET_ERROR = "setError";
export const SET_ERRORE_CARICAMENTO = "setErroreCaricamento";

// Testo usato quando l'errore non contiene un messaggio leggibile.
const MESSAGGIO_ERRORE_GENERICO = "Si è verificato un errore, riprova più tardi.";

// Normalizza qualunque errore in una stringa: Error di axios, stringa, wrapper
// {status, message, data} del filtro di licenza/permesso oppure ProblemDetails.
// Nessuna mutation deve ricevere un oggetto come messaggio di errore.
const toMessage = (err) => {
    if (typeof err === "string") {
        return err.trim() || MESSAGGIO_ERRORE_GENERICO;
    }
    if (!err) {
        return MESSAGGIO_ERRORE_GENERICO;
    }

    // Ordine di priorità: prima il messaggio del backend (ProblemDetails o
    // wrapper {status, message, data} del filtro di permesso/CheckLicenseFilter),
    // poi le varianti piatte, e SOLO ALLA FINE err.message: su un 403 axios reale
    // err.message è il generico "Request failed with status code 403" e
    // nasconderebbe il messaggio utile restituito dal backend.
    const candidati = [
        err.response?.data?.message,
        err.response?.data?.detail,
        err.response?.data?.title,
        err.response?.data,
        err.data?.message,
        err.data?.detail,
        err.data?.title,
        // Varianti piatte senza wrapper (retrocompatibilità).
        err.detail,
        err.title,
        err.message,
    ];
    const messaggio = candidati.find(
        (valore) => typeof valore === "string" && valore.trim() !== ""
    );
    if (messaggio) {
        return messaggio.trim();
    }

    // Ultima istanza: serializzazione leggibile di un oggetto senza campi noti.
    try {
        const serializzato = JSON.stringify(err);
        if (serializzato && serializzato !== "{}" && serializzato !== "[]") {
            return serializzato;
        }
    } catch {
        // Oggetto non serializzabile (riferimenti circolari): resta il messaggio generico.
    }

    return MESSAGGIO_ERRORE_GENERICO;
};

const state = {
    apiKeys: [],
    apiScopes: [],
    loading: false,
    error: {},
    // Errore del caricamento della lista: stringa (messaggio) oppure null.
    // Distingue la lista vuota dal caricamento fallito.
    erroreCaricamento: null,
};

const getters = {
    getApiKeys: (state) => state.apiKeys,
    getApiScopes: (state) => state.apiScopes,
    getLoading: (state) => state.loading,
    // Sempre stringa o null: mai un oggetto (wrapper o ProblemDetails).
    getError: (state) => {
        if (typeof state.error === "string") {
            return state.error;
        }
        if (!state.error || !Object.keys(state.error).length) {
            return null;
        }
        return toMessage(state.error);
    },
    getErroreCaricamento: (state) => state.erroreCaricamento,
};

const actions = {
    // Elenco API key di un cliente. Endpoint: GET ApiKey?clienteId={id}
    [LIST_API_KEYS](context, clienteId) {
        if (!clienteId) {
            // Il backend richiede clienteId > 0: senza selezione la lista è vuota.
            context.commit(SET_LIST_API_KEYS, []);
            context.commit(SET_LOADING, false);
            context.commit(SET_ERRORE_CARICAMENTO, null);
            return Promise.resolve([]);
        }
        context.commit(SET_LOADING, true);
        context.commit(SET_ERRORE_CARICAMENTO, null);
        return new Promise((resolve) => {
            ApiService.get(`ApiKey?clienteId=${clienteId}`)
                .then(({ data }) => {
                    context.commit(SET_LIST_API_KEYS, data);
                    context.commit(SET_LOADING, false);
                    context.commit(SET_ERRORE_CARICAMENTO, null);
                    resolve(data);
                })
                .catch((error) => {
                    // Svuota la lista committata: senza questo, la tabella
                    // continuerebbe a mostrare le righe del cliente precedente
                    // (dati stantii, anche di un cliente diverso) mentre l'alert
                    // dice che il caricamento è fallito.
                    context.commit(SET_LIST_API_KEYS, []);
                    // L'errore — es. 403 di permesso — viene tracciato a parte:
                    // la vista distingue la lista vuota dal caricamento fallito.
                    const messaggio = toMessage(error);
                    context.commit(SET_ERROR, messaggio);
                    context.commit(SET_ERRORE_CARICAMENTO, messaggio);
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    context.commit(SET_LOADING, false);
                    resolve([]);
                });
        });
    },

    // Catalogo scope disponibili. Endpoint: GET ApiKey/scopes
    [LIST_API_SCOPES](context) {
        return new Promise((resolve) => {
            ApiService.get(`ApiKey/scopes`)
                .then(({ data }) => {
                    context.commit(SET_LIST_API_SCOPES, data);
                    resolve(data);
                })
                .catch((error) => {
                    context.commit(SET_ERROR, toMessage(error));
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    resolve([]);
                });
        });
    },

    // Utenti selezionabili come utente associato della chiave (cliente e
    // sotto-clienti). Endpoint: GET ApiKey/utenti. Non entra nello store: dipende
    // dal cliente del form e vale solo finché il dialog è aperto.
    [LIST_API_UTENTI](context, clienteId) {
        return new Promise((resolve) => {
            ApiService.get(`ApiKey/utenti?clienteId=${clienteId}`)
                .then(({ data }) => resolve(Array.isArray(data) ? data : []))
                .catch((error) => {
                    context.commit(SET_ERROR, toMessage(error));
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    resolve([]);
                });
        });
    },

    // Crea una API key. Endpoint: POST ApiKey
    // La risposta contiene la chiave in chiaro (apiKey): viene restituita al
    // chiamante e NON viene salvata nello store, per non trattenerla più del necessario.
    [CREATE_API_KEY](context, params) {
        return new Promise((resolve, reject) => {
            ApiService.post(`ApiKey`, params)
                .then(({ data }) => {
                    resolve(data);
                })
                .catch((error) => {
                    context.commit(SET_ERROR, toMessage(error));
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    reject(error);
                });
        });
    },

    // Aggiorna una API key. Endpoint: PUT ApiKey/{id}
    [UPDATE_API_KEY](context, params) {
        return new Promise((resolve, reject) => {
            // Il body dell'endpoint PUT accetta solo i campi di UpdateApiKeyRequest
            // {nome, idUtente, attiva, dataScadenza, scopes}: l'id viaggia solo nella rotta.
            const { id, ...body } = params;
            ApiService.update(`ApiKey`, id, body)
                .then(({ data }) => {
                    resolve(data);
                })
                .catch((error) => {
                    context.commit(SET_ERROR, toMessage(error));
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    reject(error);
                });
        });
    },

    // Elimina una API key. Endpoint: DELETE ApiKey/{id}
    [DELETE_API_KEY](context, id) {
        return new Promise((resolve, reject) => {
            ApiService.delete(`ApiKey`, id)
                .then(() => {
                    resolve(true);
                })
                .catch((error) => {
                    context.commit(SET_ERROR, toMessage(error));
                    setTimeout(() => context.commit(SET_ERROR, ""), 5000);
                    reject(error);
                });
        });
    },
};

const mutations = {
    [SET_LIST_API_KEYS](state, apiKeys) {
        state.apiKeys = Array.isArray(apiKeys) ? apiKeys : [];
    },
    [SET_LIST_API_SCOPES](state, apiScopes) {
        state.apiScopes = Array.isArray(apiScopes) ? apiScopes : [];
    },
    [SET_LOADING](state, loading) {
        state.loading = loading;
    },
    [SET_ERROR](state, error) {
        state.error = error;
    },
    // Sempre null oppure stringa: distingue la lista vuota dal caricamento fallito.
    [SET_ERRORE_CARICAMENTO](state, valore) {
        state.erroreCaricamento =
            typeof valore === "string" && valore !== "" ? valore : null;
    },
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
};
